import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authService } from '../services/api';
import { API_BASE as API, getToken } from '../config';

interface ProfileData {
  full_name: string;
  email: string;
  phone: string;
  location: string;
  current_company: string;
  linkedin_url: string;
  github_url: string;
  portfolio_url: string;
}

const SettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const [formData, setFormData] = useState<ProfileData>({
    full_name: '',
    email: '',
    phone: '',
    location: '',
    current_company: '',
    linkedin_url: '',
    github_url: '',
    portfolio_url: ''
  });

  // Cargar datos del usuario
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const response = await authService.getProfile();
        const userData = response.data;
        
        setFormData({
          full_name: userData.full_name || '',
          email: userData.email || '',
          phone: userData.phone || '',
          location: userData.location || '',
          current_company: userData.current_company || '',
          linkedin_url: userData.linkedin_url || '',
          github_url: userData.github_url || '',
          portfolio_url: userData.portfolio_url || ''
        });
      } catch (error) {
        console.error('Error loading user data:', error);
        setMessage({ type: 'error', text: 'Error al cargar los datos del perfil' });
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const response = await authService.updateProfile(formData);
      
      setMessage({ 
        type: 'success', 
        text: '✅ Perfil actualizado correctamente' 
      });
      
      // Actualizar los datos del formulario con la respuesta del servidor
      if (response.data.user) {
        setFormData({
          full_name: response.data.user.full_name || '',
          email: response.data.user.email || '',
          phone: response.data.user.phone || '',
          location: response.data.user.location || '',
          current_company: response.data.user.current_company || '',
          linkedin_url: response.data.user.linkedin_url || '',
          github_url: response.data.user.github_url || '',
          portfolio_url: response.data.user.portfolio_url || ''
        });
      }
    } catch (error: any) {
      console.error('Error updating profile:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Error al actualizar el perfil' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof ProfileData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Cargando...</div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-8">⚙️ Configuración</h1>
      
      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      <div className="bg-white p-6 rounded-lg shadow">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Nombre Completo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre Completo
            </label>
            <input 
              type="text" 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.full_name}
              onChange={(e) => handleChange('full_name', e.target.value)}
              placeholder="Ej: Ana García López"
            />
          </div>

          {/* Email (solo lectura) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input 
              type="email" 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
              value={formData.email}
              disabled
            />
            <p className="text-xs text-gray-500 mt-1">El email no se puede cambiar</p>
          </div>

          {/* Teléfono */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Teléfono
            </label>
            <input 
              type="tel" 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="Ej: +34 612 345 678"
            />
          </div>

          {/* Ubicación */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ubicación
            </label>
            <input 
              type="text" 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.location}
              onChange={(e) => handleChange('location', e.target.value)}
              placeholder="Ej: Madrid, España"
            />
          </div>

          {/* LinkedIn URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              LinkedIn URL
            </label>
            <input 
              type="url" 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.linkedin_url}
              onChange={(e) => handleChange('linkedin_url', e.target.value)}
              placeholder="Ej: https://www.linkedin.com/in/bensmith"
            />
            <p className="text-xs text-gray-500 mt-1">
              Tu perfil de LinkedIn (usado para auto-aplicar a jobs)
            </p>
          </div>

          {/* Current Company */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Empresa Actual
            </label>
            <input 
              type="text" 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.current_company}
              onChange={(e) => handleChange('current_company', e.target.value)}
              placeholder="Ej: Tech Corp Inc. (o deja vacío si estás buscando empleo)"
            />
            <p className="text-xs text-gray-500 mt-1">
              Tu empresa actual (usado para completar formularios automáticamente)
            </p>
          </div>

          {/* GitHub URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              GitHub URL (Opcional)
            </label>
            <input 
              type="url" 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.github_url}
              onChange={(e) => handleChange('github_url', e.target.value)}
              placeholder="Ej: https://github.com/bensmith"
            />
            <p className="text-xs text-gray-500 mt-1">
              Tu perfil de GitHub (útil para aplicaciones técnicas)
            </p>
          </div>

          {/* Portfolio URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Portfolio URL (Opcional)
            </label>
            <input 
              type="url" 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.portfolio_url}
              onChange={(e) => handleChange('portfolio_url', e.target.value)}
              placeholder="Ej: https://bensmith.dev"
            />
            <p className="text-xs text-gray-500 mt-1">
              Tu sitio web o portfolio personal
            </p>
          </div>

          {/* Botón de guardar */}
          <button 
            type="submit" 
            disabled={saving}
            className={`w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors ${
              saving 
                ? 'opacity-50 cursor-not-allowed' 
                : 'hover:bg-blue-700'
            }`}
          >
            {saving ? '💾 Guardando...' : '💾 Guardar Cambios'}
          </button>
        </form>
      </div>

      {/* API keys — for the public Veritas API */}
      <ApiKeysPanel />

      {/* Información de debug (solo en desarrollo) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-6 bg-gray-100 p-4 rounded-lg">
          <h3 className="font-bold mb-2">Debug Info:</h3>
          <pre className="text-xs overflow-auto">
            {JSON.stringify(formData, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

// ── API keys panel ───────────────────────────────────────────────────────────
interface ApiKey { id: number; name: string; key_prefix: string; last_used_at: string | null; revoked: number; created_at: string; }

const ApiKeysPanel: React.FC = () => {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null); // shown once

  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  const load = () => {
    fetch(`${API}/api/api-keys`, { headers: headers() })
      .then(r => r.json()).then(d => setKeys(d.keys || [])).catch(() => {});
  };
  useEffect(load, []);

  const create = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/api-keys`, {
        method: 'POST', headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() || 'API key' }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Could not create key');
      setNewKey(d.key);
      setName('');
      load();
    } catch (e: any) { toast.error(e.message); }
    finally { setCreating(false); }
  };

  const revoke = async (id: number) => {
    if (!window.confirm('Revoke this key? Apps using it will stop working.')) return;
    await fetch(`${API}/api/api-keys/${id}`, { method: 'DELETE', headers: headers() });
    load();
  };

  return (
    <div className="mt-8 bg-white p-6 rounded-lg shadow">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xl font-bold text-gray-900">API keys</h2>
        <Link to="/api-docs" className="text-sm text-blue-600 hover:underline">API docs →</Link>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Call the Veritas API (CV generation with truth-check) from your own apps. Send the key as the
        <code className="mx-1 px-1 bg-gray-100 rounded">X-API-Key</code> header.
      </p>

      {newKey && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-xs font-semibold text-amber-800 mb-1">Copy your key now — it won't be shown again:</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-white border border-amber-200 rounded px-2 py-1 break-all">{newKey}</code>
            <button onClick={() => { navigator.clipboard.writeText(newKey); toast.success('Copied'); }}
              className="text-xs px-3 py-1.5 bg-amber-600 text-white rounded-lg">Copy</button>
            <button onClick={() => setNewKey(null)} className="text-xs text-gray-500">Done</button>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Key name (e.g. my-bot)"
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        <button onClick={create} disabled={creating}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
          {creating ? 'Creating…' : 'Create key'}
        </button>
      </div>

      {keys.length === 0 ? (
        <p className="text-sm text-gray-400">No keys yet.</p>
      ) : (
        <div className="space-y-2">
          {keys.map(k => (
            <div key={k.id} className={`flex items-center justify-between border rounded-lg px-3 py-2 ${k.revoked ? 'opacity-50 border-gray-100' : 'border-gray-200'}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{k.name}</p>
                <p className="text-xs text-gray-400">
                  <code>{k.key_prefix}…</code> · created {new Date(k.created_at).toLocaleDateString()}
                  {k.last_used_at ? ` · last used ${new Date(k.last_used_at).toLocaleDateString()}` : ' · never used'}
                  {k.revoked ? ' · revoked' : ''}
                </p>
              </div>
              {!k.revoked && (
                <button onClick={() => revoke(k.id)} className="text-xs text-red-600 hover:underline shrink-0">Revoke</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SettingsPage;